import { Badge } from "react-bootstrap";
import RdrChips from "../components/home/RdrChips.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import WorkflowReport from "../components/reports/WorkflowReport.jsx";
import SlaReport from "../components/reports/SlaReport.jsx";
import RdrMainReport from "../components/reports/RdrMainReport.jsx";
import RdrAccountsReport from "../components/reports/RdrAccountsReport.jsx";
import RdrHsReport from "../components/reports/RdrHsReport.jsx";
import "../styles/home.css";

// Live report component per section. Rendered whenever the reports master
// switch (System Data) is ON — the old per-page "Live report" toggle is gone
// (2026-07-12): one switch controls every report surface.
// Marketing on WFM: back as of 2026-07-17 (1.0 parity) — WorkflowReport
// renders the self-gating MarketingCallsPanel whenever the marketing setting
// is on, so the toggle governs everywhere.
const LIVE_COMPONENTS = {
  workflow: WorkflowReport,
  sla: SlaReport,
  main: RdrMainReport,
  accounts: RdrAccountsReport,
  "health-safety": RdrHsReport,
};

// The RDR landing pages (Main / Accounts / Health & Safety) — the manager
// "system health" view, kept as the SAME three groups the 1.0 RDR used so it
// reads as familiar. Deliberately thin: Jobs Logged is live (reuses the home
// stats), everything else lists the 1.0 section it will replace, marked TBD.
// The plan is for these counts to fall out of the next-action badge engine
// (one aggregate per rule), NOT a port of the 50-query legacy page.

const SECTIONS = {
  workflow: {
    title: "Workflow Management",
    live: true,
    // Shown under the LIVE view: counters that still need legacy data ported.
    liveDeferred: [], // Marketing moved to Dashboard + /marketing (2026-07-15)
    // Shown when the toggle is OFF: the full familiar TBD listing.
    items: [
      { name: "No Initial Contact", detail: "Requested jobs where no first contact has been made" },
      { name: "No Appointment Made", detail: "Requested jobs past their request date with no inspection booked" },
      { name: "Inspections To Confirm", detail: "Scheduled inspections not yet confirmed" },
      { name: "Outstanding Inspections", detail: "Confirmed inspections past their date, not yet inspected" },
      { name: "Inspected Not Typed", detail: "Inspected jobs with no typed quote" },
      { name: "Typed Not Checked", detail: "Typed quotes waiting to be checked" },
      { name: "Checked Ready To Send", detail: "Checked quotes ready to go out" },
      { name: "Quote Follow Up", detail: "Quoted with no follow-up in 5 working days" },
      { name: "Contractor Quoting Follow Up", detail: "Jobs out with contractors for pricing" },
      { name: "Declined Jobs Without Reasons", detail: "Declined with no reason recorded" },
      { name: "Declined Need To Invoice", detail: "Declined jobs still owed a quote-fee invoice" },
    ],
  },
  sla: {
    title: "SLA",
    live: true,
    // Exempt from the reports master switch (Robert 2026-07-19) — like Jobs
    // Logged / Accepted and the Dashboard header, SLA always shows.
    // Both former deferrals (OOT reasons excuse workflow, insurer/assessor
    // filters) are BUILT — see services/slaReport.js + SlaReport.jsx.
    liveDeferred: [],
    items: [
      { name: "Job Logged → First Contact", detail: "1 working day · 100% target" },
      { name: "First Contact → Scheduled Inspection", detail: "3 working days · 90% target" },
      { name: "Scheduled Inspection → Job Inspected", detail: "1 working day · 90% target" },
      { name: "Job Inspected → Inspection Typed", detail: "1 working day · 90% target" },
      { name: "Inspection Typed → Quote Checked", detail: "1 working day · 90% target" },
      { name: "Quote Checked → Quote Sent", detail: "Same day · 100% target" },
      { name: "Job Inspected → Quote Sent (total)", detail: "2 working days · 90% target" },
    ],
  },
  main: {
    title: "PCO Dashboard",
    live: true,
    // Exempt from the reports master switch (Robert 2026-07-25 — Karen wants to
    // demo it) — like the SLA tab / Jobs Logged / Dashboard header, the PCO
    // Dashboard always shows regardless of the toggle.
    // Both former deferrals BUILT 2026-07-12: assessor_update_overdue rule
    // (per-company thresholds) + MarketingCallsPanel.
    liveDeferred: [],
    items: [
      { name: "Jobs Logged / Accepted vs Targets", detail: "Today 10 · week 50 · month 200" },
      { name: "SLA Compliance", detail: "Working-day turnaround between milestones" },
      { name: "Client Updates Not Completed", detail: "Accepted jobs with no client update in 5 business days" },
      { name: "Assessor Updates Not Completed", detail: "Per-assessor-company update thresholds" },
      { name: "Problem & Important Jobs", detail: "Flagged jobs without a review today" },
      { name: "Accepted Jobs With No Daily Review", detail: "In-progress jobs missing their daily review" },
      { name: "On Hold To Review", detail: "On-hold jobs past their review date" },
      { name: "Overdue Start / End Dates", detail: "Estimated dates passed with no actual date" },
      { name: "Overdue Closure Calls / Lockboxes", detail: "Recently completed jobs missing wrap-up steps" },
      { name: "Adverse Feedback Needing Resolution", detail: "Open complaints without a resolution" },
    ],
  },
  accounts: {
    title: "Accounts",
    live: true,
    liveDeferred: [],
    items: [
      { name: "Monthly Invoice Totals", detail: "Per division, ex GST, with quote fees split out" },
      { name: "Overruns To Be Approved", detail: "Contractor invoices exceeding contract given" },
      { name: "Non-Approved Contractor Invoices", detail: "Awaiting approval, with dollar totals" },
      { name: "Disputed Invoices", detail: "Invoices flagged as disputed" },
      { name: "Excess Invoices To Raise / Unpaid", detail: "The excess lifecycle's loose ends" },
      { name: "Completed With No Invoice", detail: "Completed jobs not yet billed" },
      { name: "PC Sum Actuals To Enter", detail: "PC sums with no actual cost recorded" },
      { name: "Jobs With Overdue Payments", detail: "Including overdue with no progress" },
      { name: "WIP In Progress Jobs", detail: "Net work-in-progress per project manager" },
    ],
  },
  "health-safety": {
    title: "Health & Safety",
    live: true,
    liveDeferred: [],
    items: [
      { name: "Orders With Overdue Health & Safety", detail: "Invoiced orders missing risk assessment or completed photos" },
      { name: "Companies With Liability Expiring", detail: "Public liability expiring within a month" },
      { name: "Contractors Without Public Liability", detail: "Contractors on open jobs with expired or missing PL" },
    ],
  },
};

// TBD row list (shared by the placeholder view and the live view's deferred block).
function TbdList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="placeholder-panel">
      {items.map(({ name, detail }) => (
        <div
          key={name}
          className="d-flex flex-wrap justify-content-between align-items-center column-gap-3 py-2 border-bottom"
        >
          <div>
            <div className="fw-semibold">{name}</div>
            <div className="small text-muted">{detail}</div>
          </div>
          <Badge bg="light" text="secondary" className="border">
            TBD
          </Badge>
        </div>
      ))}
    </div>
  );
}

// Section key → "?" business-rules topic (businessRules.js).
const HELP_TOPICS = {
  workflow: "workflow",
  sla: "sla",
  main: "rdrMain",
  accounts: "accounts",
  "health-safety": "healthSafety",
};

function RdrPage({ section }) {
  const current = SECTIONS[section];

  return (
    <section className="page-panel">
      {/* One header line: heading (fixed lead so chips don't jump between screens)
          · chips · Live report pushed to the far right (ms-auto).
          SLA prints its own report masthead, so its app heading stays off the
          paper; the chips are navigation and never print (no-print in RdrChips). */}
      <div className={`d-flex flex-wrap align-items-center column-gap-4 row-gap-2${section === "sla" ? " no-print" : ""}`}>
        <div className="page-heading rdr-chip-lead">
          <h2>{current.title} <RulesHelp topic={HELP_TOPICS[section]} /></h2>
        </div>
        <RdrChips active={section} />
      </div>

      {/* `live` alone now. Every section used to be held behind the reports
          master switch and every one of them had been granted `alwaysLive` to
          get out from under it, which made the pair a no-op long before the
          switch was removed on 2026-08-07. `items` below is still what an
          unbuilt section shows. */}
      {current.live ? (
        <>
          {(() => {
            const Live = LIVE_COMPONENTS[section];
            return Live ? <Live /> : null;
          })()}
          <TbdList items={current.liveDeferred} />
        </>
      ) : (
        <TbdList items={current.items} />
      )}
    </section>
  );
}

export default RdrPage;
